import { Calendar, Clock, Globe } from 'lucide-react'

import { TIMEZONES_BR } from '@/lib/timezones-br'

interface BusinessHoursValue {
  timezone: string
  start: string
  end: string
  days: string[]
}

const DAY_LABELS: Readonly<Record<string, string>> = {
  mon: 'Seg',
  tue: 'Ter',
  wed: 'Qua',
  thu: 'Qui',
  fri: 'Sex',
  sat: 'Sáb',
  sun: 'Dom',
}

/**
 * Story 3.16.1 — Display read-only de `business_hours` na listagem
 * `/admin/settings`. Mostra 3 linhas estruturadas (dias / horário / TZ)
 * com ícones lucide. Espelha o "Resumo" do modal de edição para manter
 * consistência visual entre as 2 surfaces (listagem vs edição).
 */
export function BusinessHoursDisplay({ value }: { value: unknown }): React.ReactElement {
  if (!isBusinessHoursShape(value)) {
    return <code className="text-xs text-muted-foreground">(formato inválido)</code>
  }
  const daysLabel = value.days
    .map((d) => DAY_LABELS[d] ?? d)
    .join(' • ')
  const hours = computeHoursPerDay(value.start, value.end)
  const tzLabel =
    TIMEZONES_BR.find((tz) => tz.value === value.timezone)?.label.replace(/^🇧🇷\s/, '') ??
    value.timezone

  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-center gap-2">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <span>{daysLabel || '(nenhum dia selecionado)'}</span>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <span>
          {value.start} → {value.end}
          {hours > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">({formatHours(hours)}/dia)</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Globe className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <span className="text-muted-foreground">{tzLabel}</span>
      </div>
    </div>
  )
}

function isBusinessHoursShape(v: unknown): v is BusinessHoursValue {
  if (v === null || typeof v !== 'object') return false
  const obj = v as Record<string, unknown>
  return (
    typeof obj.timezone === 'string' &&
    typeof obj.start === 'string' &&
    typeof obj.end === 'string' &&
    Array.isArray(obj.days) &&
    obj.days.every((d) => typeof d === 'string')
  )
}

function computeHoursPerDay(start: string, end: string): number {
  if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0
  const startMin = (sh as number) * 60 + (sm as number)
  const endMin = (eh as number) * 60 + (em as number)
  if (endMin <= startMin) return 0
  return (endMin - startMin) / 60
}

function formatHours(h: number): string {
  if (Math.abs(h - Math.round(h)) < 0.01) return `${Math.round(h)}h`
  return `${h.toFixed(1)}h`
}
